import React from 'react';
import { AlignItems, CFlex, JustifyContent } from '@/src/components/c-flex/CFlex';
import { CBox } from '@/src/components/c-box/CBox';
import { CButton, CButtonPattern, CButtonShape, CButtonSize } from '@/src/components/c-button/CButton';
import { CText, CTextSize, CTextWeight } from '@/src/components/c-text/CText';
import { CDivider, CDividerColor } from '@/src/components/c-divider/CDivider';
import { CIcons } from '@/src/components/c-icon/CIcon';
import { CSpacing, NativeProps } from '@/src/components/interfaces';
import { PresenterStore } from '@/src/infrastructure/presenter/store';
import classNames from 'classnames';

export enum CModalWidth {
  Sm = 'sm',
  Md = 'md',
  Lg = 'lg',
}

export interface CModalProps<TInput = void, TOutput = void> extends NativeProps {
  presenter: PresenterStore<TInput, TOutput>;
  width?: CModalWidth;
  header?: {
    title: string;
    slot?: React.JSX.Element;
  };
  content: {
    slot: React.JSX.Element;
  };
  scrollable?: boolean;
  footer?: {
    slot?: React.JSX.Element;
  };
}

export function CModal<TInput = void, TOutput = void>(props: CModalProps<TInput, TOutput>): React.JSX.Element | null {
  if (!props.presenter.state.rendered) {
    return null;
  }

  return (
    <CFlex
      class={classNames('c-modal__curtain', {
        'c-modal__curtain--visible': props.presenter.state.visible,
      })}
      justifyContent={JustifyContent.Center}
      alignItems={AlignItems.Center}
      onClick={(e: Event) => { e.stopPropagation(); props.presenter.reject(); }}
    >
      <dialog
        open
        className={classNames('c-modal', `c-modal--width-${props.width ?? CModalWidth.Md}`, props.class, {
          'c-modal--visible': props.presenter.state.visible,
        })}
        id={props.id}
        data-test-selector={props.testSelector}
        onClick={(e) => e.stopPropagation()}
        aria-modal="true"
      >
        <div className="c-modal__grid">
          <CBox class="c-modal__grid__header c-padding--horizontal--xlg c-padding--vertical--md">
            <CFlex justifyContent={JustifyContent.SpaceBetween} alignItems={AlignItems.Center} fullHeight>
              <CFlex alignItems={AlignItems.Center}>
                {props.header?.title && (
                  <CText size={CTextSize.Md} weight={CTextWeight.SemiBold}>{props.header.title}</CText>
                )}
                {props.header?.slot}
              </CFlex>
              <CButton
                icon={CIcons.X}
                size={CButtonSize.Sm}
                pattern={CButtonPattern.Secondary}
                shape={CButtonShape.Square}
                onClick={(e) => { e.stopPropagation(); props.presenter.reject(); }}
              />
            </CFlex>
          </CBox>

          <CDivider color={CDividerColor.Light} marginVertical={CSpacing.None} marginHorizontal={CSpacing.Xlg} />

          <CBox class={classNames('c-modal__grid__content c-padding--xlg', { 'overflow--x-hidden overflow--y-auto': props.scrollable !== false })}>
            {props.content.slot}
          </CBox>

          {props.footer?.slot && (
            <>
              <CDivider color={CDividerColor.Light} marginVertical={CSpacing.None} marginHorizontal={CSpacing.Xlg} />
              <CBox class="c-modal__grid__footer c-padding--horizontal--xlg c-padding--vertical--md">
                <CFlex alignItems={AlignItems.Center} justifyContent={JustifyContent.FlexEnd} fullHeight>
                  {props.footer.slot}
                </CFlex>
              </CBox>
            </>
          )}
        </div>
      </dialog>
    </CFlex>
  );
}
